/**
 * GPS Data Provider
 * Central data management layer between GPS WebSocket SDK and UI components
 * Handles data processing, optimization, caching, and distribution to subscribers
 */

import { GpsWebSocketSDK, type DeviceGpsInfo } from '../gps-websocket';
import { getAuthConfigFromStore } from '../auth/authUtils';
import type {
  GpsDataProviderConfig,
  GpsDataStats,
  GpsDataProviderState,
  DataProcessingResult,
} from './types';

import { DEFAULT_GPS_PROVIDER_CONFIG } from './types';

import { 
  processGpsDataUpdates,
  optimizeForMapRendering,
  calculateProcessingMetrics,
  batchProcessUpdates
} from './utils/dataOptimization';

import {
  applyAutoFilter,
  filterByStatus,
  isValidCoordinate
} from './utils/filtering';

import { GpsDataCache, generateCacheKey, CacheKeys } from './utils/caching';

/**
 * GPS Data Update callback type
 */
export type GpsDataUpdateCallback = (devices: DeviceGpsInfo[]) => void;
export type GpsConnectionCallback = (connected: boolean) => void;
export type GpsErrorCallback = (error: Error) => void;
export type GpsStatsCallback = (stats: GpsDataStats) => void;
/**
 * GPS Data Provider Class
 * Manages GPS data flow from WebSocket to UI components with optimization and caching
 */
export class GpsDataProvider {
  // Core configuration and state
  private config: GpsDataProviderConfig;
  private gpsSDK: GpsWebSocketSDK;
  private isInitialized: boolean = false;
  private isConnected: boolean = false;
  
  // Data management
  private currentDevices: DeviceGpsInfo[] = [];
  private previousDevices: DeviceGpsInfo[] = [];
  private deviceHistory: DeviceGpsInfo[][] = [];
  
  // Direct callback system (new approach)
  private updateCallbacks: Set<GpsDataUpdateCallback> = new Set();
  private connectionCallbacks: Set<GpsConnectionCallback> = new Set();
  private errorCallbacks: Set<GpsErrorCallback> = new Set();
  private statsCallbacks: Set<GpsStatsCallback> = new Set();
  
  // Performance tracking
  private stats: GpsDataStats;
  private lastUpdateTime: Date | null = null;
  private updateTimestamps: number[] = [];
  
  // Caching system
  private cache: GpsDataCache;
  
  // Batch processing
  private batchQueue: DeviceGpsInfo[][] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  
  // Rate limiting
  private lastProcessTime: number = 0;
  private processingInProgress: boolean = false;
  
  // Event system
  private eventHandlers: Map<string, Array<(data: any) => void>> = new Map();

  /**
   * Create GPS Data Provider instance
   * @param config - Provider configuration (uses AuthStore and API endpoints)
   */
  constructor(config: Partial<GpsDataProviderConfig>) {
    // Merge with default configuration
    this.config = { ...DEFAULT_GPS_PROVIDER_CONFIG, ...config } as GpsDataProviderConfig;
    
    // Create GPS WebSocket SDK (automatically gets URL from API endpoint system)
    this.gpsSDK = new GpsWebSocketSDK();
    
    // Initialize statistics
    this.stats = {
      isConnected: false,
      lastUpdateTime: null,
      updateCount: 0,
      updatesPerMinute: 0,
      averageUpdateInterval: 0,
      totalDevices: 0,
      activeDevices: 0,
      inactiveDevices: 0,
      videoSharingDevices: 0,
      processedUpdates: 0,
      skippedUpdates: 0,
      optimizationRatio: 0,
      errorCount: 0,
      lastError: null
    };
    
    // Initialize cache
    this.cache = new GpsDataCache({
      maxEntries: this.config.cacheSize,
      maxAge: 300000, // 5분
      enableCompression: true,
      autoCleanup: true
    });
    
    // Start batch processing if enabled
    if (this.config.enableBatching) {
      this.startBatchProcessing();
    }
  }

  /**
   * Initialize provider and start WebSocket connection
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('GPS Data Provider already initialized');
      return;
    }
    
    this.setupWebSocketEventHandlers();
    
    this.isInitialized = true;
    
    console.log('✅ GPS Data Provider initialized (WebSocket not connected yet)');
  }

  /**
   * Connect to GPS WebSocket
   */
  connectWebSocket(): void {
    if (!this.isInitialized) {
      throw new Error('Provider not initialized. Call initialize() first.');
    }
    
    if (this.isConnected) {
      console.warn('GPS WebSocket already connected');
      return;
    }
    
    console.log('🔗 Connecting to GPS WebSocket...');
    this.gpsSDK.connect();
  }

  /**
   * Disconnect from GPS WebSocket (but keep Provider alive)
   */
  disconnectWebSocket(): void {
    if (!this.isConnected) {
      console.warn('GPS WebSocket not connected');
      return;
    }
    
    console.log('🔌 Disconnecting from GPS WebSocket...');
    this.gpsSDK.disconnect();
    
    // Clear current data
    this.currentDevices = [];
    this.previousDevices = [];
    
    // Reset some stats but keep Provider structure
    this.stats.isConnected = false;
    this.stats.totalDevices = 0;
    this.stats.activeDevices = 0;
    this.stats.inactiveDevices = 0;
    this.stats.videoSharingDevices = 0;
    
    // Notify callbacks about disconnection
    this.notifyConnectionCallbacks(false);
    this.notifyUpdateCallbacks([]); // Empty devices array
  }

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Update authentication from AuthStore
   * Call this when AuthStore state changes
   */
  updateAuthFromStore(): void {
    // GPS WebSocket SDK automatically uses AuthStore
    // Just notify SDK to refresh its auth state
    
    if (this.gpsSDK && this.isInitialized) {
      this.gpsSDK.updateAuthFromStore();
      
      // Reconnect if currently connected to refresh authentication
      if (this.isConnected) {
        console.log('🔄 Auth updated from store, reconnecting...');
        this.gpsSDK.disconnect();
        
        // Reconnect after brief delay
        setTimeout(() => {
            this.gpsSDK.connect();
        }, 1000);
      }
    }
  }

  /**
   * Get current JWT token
   */
  getCurrentJwtToken(): string | null {
    // Always use AuthStore
    const authConfig = getAuthConfigFromStore();
    return authConfig?.jwtToken || null;
  }

  /**
   * Get current GPS device data
   * @returns Current device data
   */
  getCurrentData(): DeviceGpsInfo[] {
    return [...this.currentDevices];
  }

  /**
   * Get GPS data history
   * @returns Array of historical device data
   */
  getDataHistory(): DeviceGpsInfo[][] {
    return [...this.deviceHistory];
  }

  /**
   * Get current provider state
   * @returns Provider state
   */
  getState(): GpsDataProviderState {
    return {
      isConnected: this.isConnected,
      isInitialized: this.isInitialized,
      currentDevices: [...this.currentDevices],
      deviceCount: this.currentDevices.length,
      stats: { ...this.stats },
      callbackCount: this.updateCallbacks.size
    };
  }

  /**
   * Get GPS data for multiple specific devices (Multi Live View)
   * @param psnList - List of PSNs to track
   */
  async requestGpsPsnsMultiLiveView(psnList: string[]): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }
    
    try {
      // Check cache first
      const cacheKey = CacheKeys.multiLiveView('user', psnList);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for PSNs: ${psnList.join(', ')}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }
      
      // Request from API
      console.log(`📡 Requesting GPS data for PSNs: ${psnList.join(', ')} (should establish continuous updates)`);
      await this.gpsSDK.gpsPsnsMultiLiveView(psnList); // SDK handles auth automatically
      console.log(`✅ GPS MultiLiveView request completed for PSNs: ${psnList.join(', ')}`);
      
    } catch (error) {
      console.error(`❌ GPS MultiLiveView request failed:`, error);
      this.handleError(error instanceof Error ? error : new Error('Failed to request initial data'));
      throw error;
    }
  }

  // === GPS WEBSOCKET API METHODS (All 8 APIs) ===

  /**
   * Get GPS data for public devices in zone (no authentication required)
   * @param sw - Southwest coordinate (lat_lng format)
   * @param ne - Northeast coordinate (lat_lng format)
   */
  async requestGpsZoneNoAuth(sw: string, ne: string): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = CacheKeys.zone('user', sw, ne);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for zone: ${sw} to ${ne}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting public zone data: ${sw} to ${ne}`);
      await this.gpsSDK.gpsZoneNoAuth(sw, ne);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request zone data (no auth)'));
      throw error;
    }
  }

  /**
   * Get GPS data for user's devices in specified zone
   * @param sw - Southwest coordinate (lat_lng format)
   * @param ne - Northeast coordinate (lat_lng format)
   */
  async requestGpsZoneMyDevices(sw: string, ne: string): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      // Use cache key without user email (SDK handles auth)
      console.log(`📡 Requesting my devices in zone: ${sw} to ${ne}`);
      await this.gpsSDK.gpsZoneMyDevices(sw, ne);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request my devices in zone'));
      throw error;
    }
  }

  /**
   * Get GPS data for all devices in specified zone
   * @param sw - Southwest coordinate (lat_lng format)
   * @param ne - Northeast coordinate (lat_lng format)
   */
  async requestGpsZoneAllDevices(sw: string, ne: string): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = CacheKeys.zone('user', sw, ne);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for all devices in zone: ${sw} to ${ne}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting all devices in zone: ${sw} to ${ne}`);
      await this.gpsSDK.gpsZoneAllDevices(sw, ne);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request all devices in zone'));
      throw error;
    }
  }

  /**
   * Get GPS data for specific group devices in zone
   * @param sw - Southwest coordinate (lat_lng format)
   * @param ne - Northeast coordinate (lat_lng format)
   * @param groupIdList - Array of group IDs to filter
   */
  async requestGpsZoneGroupMyDevices(sw: string, ne: string, groupIdList: string[]): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = generateCacheKey('user', groupIdList, `zone_group_my_${sw}_${ne}`);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for group devices in zone: ${groupIdList.join(', ')}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting group devices in zone: ${groupIdList.join(', ')}`);
      await this.gpsSDK.gpsZoneGroupMyDevices(sw, ne, groupIdList);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request group devices in zone'));
      throw error;
    }
  }

  /**
   * Get GPS data for all devices in specific groups within zone
   * @param sw - Southwest coordinate (lat_lng format)
   * @param ne - Northeast coordinate (lat_lng format)
   * @param groupIdList - Array of group IDs to filter
   */
  async requestGpsZoneGroupAllDevices(sw: string, ne: string, groupIdList: string[]): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = generateCacheKey('user', groupIdList, `zone_group_all_${sw}_${ne}`);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for all group devices in zone: ${groupIdList.join(', ')}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting all group devices in zone: ${groupIdList.join(', ')}`);
      await this.gpsSDK.gpsZoneGroupAllDevices(sw, ne, groupIdList);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request all group devices in zone'));
      throw error;
    }
  }

  /**
   * Get GPS data for all user's devices
   */
  async requestGpsPsnsMyDevices(): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = CacheKeys.myDevices('user');
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for my devices`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting all my devices`);
      await this.gpsSDK.gpsPsnsMyDevices();
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request my devices'));
      throw error;
    }
  }

  /**
   * Get GPS data for a specific device
   * @param psn - Device PSN (Product Serial Number)
   */
  async requestGpsPsnsSpecificDevice(psn: string): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      const cacheKey = CacheKeys.specificDevice('user', psn);
      const cachedData = this.cache.get(cacheKey);
      
      if (cachedData) {
        console.log(`📦 Using cached data for device: ${psn}`);
        this.processIncomingData(cachedData, 'cache');
        return;
      }

      console.log(`📡 Requesting specific device: ${psn}`);
      await this.gpsSDK.gpsPsnsSpecificDevice(psn);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error('Failed to request specific device'));
      throw error;
    }
  }

  // === DIRECT CALLBACK SYSTEM (Hook-free approach) ===

  /**
   * Register callback for GPS data updates
   * @param callback - Function to call when GPS data updates
   */
  registerUpdateCallback(callback: GpsDataUpdateCallback): void {
    this.updateCallbacks.add(callback);
    console.log(`📡 Update callback registered (${this.updateCallbacks.size} total)`);
    
    // Send current data immediately if available
    if (this.currentDevices.length > 0) {
      try {
        callback(this.currentDevices);
      } catch (error) {
        console.error('Error in immediate update callback:', error);
      }
    }
  }

  /**
   * Unregister GPS data update callback
   * @param callback - Function to remove from callbacks
   */
  unregisterUpdateCallback(callback: GpsDataUpdateCallback): void {
    const removed = this.updateCallbacks.delete(callback);
    if (removed) {
      console.log(`📡 Update callback unregistered (${this.updateCallbacks.size} remaining)`);
    }
  }

  /**
   * Register callback for connection status changes
   * @param callback - Function to call when connection status changes
   */
  registerConnectionCallback(callback: GpsConnectionCallback): void {
    this.connectionCallbacks.add(callback);
    console.log(`🔗 Connection callback registered (${this.connectionCallbacks.size} total)`);
    
    // Send current connection status immediately
    try {
      callback(this.isConnected);
    } catch (error) {
      console.error('Error in immediate connection callback:', error);
    }
  }

  /**
   * Unregister connection status callback
   * @param callback - Function to remove from callbacks
   */
  unregisterConnectionCallback(callback: GpsConnectionCallback): void {
    const removed = this.connectionCallbacks.delete(callback);
    if (removed) {
      console.log(`🔗 Connection callback unregistered (${this.connectionCallbacks.size} remaining)`);
    }
  }

  /**
   * Register callback for error notifications
   * @param callback - Function to call when errors occur
   */
  registerErrorCallback(callback: GpsErrorCallback): void {
    this.errorCallbacks.add(callback);
    console.log(`❌ Error callback registered (${this.errorCallbacks.size} total)`);
  }

  /**
   * Unregister error callback
   * @param callback - Function to remove from callbacks
   */
  unregisterErrorCallback(callback: GpsErrorCallback): void {
    const removed = this.errorCallbacks.delete(callback);
    if (removed) {
      console.log(`❌ Error callback unregistered (${this.errorCallbacks.size} remaining)`);
    }
  }

  /**
   * Register callback for statistics updates
   * @param callback - Function to call when stats update
   */
  registerStatsCallback(callback: GpsStatsCallback): void {
    this.statsCallbacks.add(callback);
    console.log(`📊 Stats callback registered (${this.statsCallbacks.size} total)`);
    
    // Send current stats immediately
    try {
      callback(this.stats);
    } catch (error) {
      console.error('Error in immediate stats callback:', error);
    }
  }

  /**
   * Unregister statistics callback
   * @param callback - Function to remove from callbacks
   */
  unregisterStatsCallback(callback: GpsStatsCallback): void {
    const removed = this.statsCallbacks.delete(callback);
    if (removed) {
      console.log(`📊 Stats callback unregistered (${this.statsCallbacks.size} remaining)`);
    }
  }

  /**
   * Clear all callbacks (useful for cleanup)
   */
  clearAllCallbacks(): void {
    this.updateCallbacks.clear();
    this.connectionCallbacks.clear();
    this.errorCallbacks.clear();
    this.statsCallbacks.clear();
    console.log('🧹 All callbacks cleared');
  }

  /**
   * Request data using any GPS WebSocket action (Generic method)
   * @param action - GPS WebSocket action name
   * @param params - Action-specific parameters
   */
  async requestGpsData(action: string, params: any = {}): Promise<void> {
    if (!this.isConnected) {
      throw new Error('GPS WebSocket not connected');
    }

    try {
      console.log(`📡 Requesting GPS data: ${action}`, params);
      
      switch (action) {
        case 'gpsZoneNoAuth':
          await this.requestGpsZoneNoAuth(params.sw, params.ne);
          break;
        case 'gpsZoneMyDevices':
          await this.requestGpsZoneMyDevices(params.sw, params.ne);
          break;
        case 'gpsZoneAllDevices':
          await this.requestGpsZoneAllDevices(params.sw, params.ne);
          break;
        case 'gpsZoneGroupMyDevices':
          await this.requestGpsZoneGroupMyDevices(params.sw, params.ne, params.groupIdList);
          break;
        case 'gpsZoneGroupAllDevices':
          await this.requestGpsZoneGroupAllDevices(params.sw, params.ne, params.groupIdList);
          break;
        case 'gpsPsnsMyDevices':
          await this.requestGpsPsnsMyDevices();
          break;
        case 'gpsPsnsSpecificDevice':
          await this.requestGpsPsnsSpecificDevice(params.psn);
          break;
        case 'gpsPsnsMultiLiveView':
          await this.requestGpsPsnsMultiLiveView(params.psnList);
          break;
        default:
          throw new Error(`Unsupported GPS action: ${action}`);
      }
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(`Failed to execute GPS action: ${action}`));
      throw error;
    }
  }

  /**
   * Destroy provider and cleanup resources
   */
  destroy(): void {
    // Stop batch processing
    this.stopBatchProcessing();
    
    // Disconnect and cleanup GPS SDK
    if (this.gpsSDK) {
      this.gpsSDK.disconnect();
    }
    
    // Destroy cache
    this.cache.destroy();
    
    // Clear data
    this.currentDevices = [];
    this.previousDevices = [];
    this.deviceHistory = [];
    
    this.isInitialized = false;
    console.log('🗑️ GPS Data Provider destroyed');
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupWebSocketEventHandlers(): void {
    // Connection events
    this.gpsSDK.on('open', () => {
      this.isConnected = true;
      this.stats.isConnected = true;
      this.notifyConnectionChange(true);
      console.log('🟢 [Provider] GPS WebSocket connected');
    });
    
    this.gpsSDK.on('close', () => {
      this.isConnected = false;
      this.stats.isConnected = false;
      this.notifyConnectionChange(false);
      console.log('🔴 [Provider] GPS WebSocket disconnected');
    });
    
    this.gpsSDK.on('error', (error) => {
      this.handleError(error instanceof Error ? error : new Error('WebSocket error'));
    });
    
    // Raw message logging for debugging
    this.gpsSDK.on('raw', (rawMsg) => {
      console.log('🔍 [Provider] GPS Provider received raw message:', {
        resultcode: rawMsg.resultcode,
        hasDeviceData: !!(rawMsg.response?.device_list || rawMsg.response?.device),
        deviceCount: rawMsg.response?.device_list?.length || (rawMsg.response?.device ? 1 : 0),
        sequenceId: rawMsg.response?.sequenceId,
        timestamp: new Date().toISOString()
      });
    });
    
    // Data events
    this.gpsSDK.on('deviceGps', (devices: DeviceGpsInfo[]) => {
      console.log(`📊 [Provider] Received deviceGps event: ${devices.length} devices at ${new Date().toISOString()}`);
      this.processIncomingData(devices, 'websocket');
    });
  }

  /**
   * Process incoming GPS data
   * @param devices - Raw GPS device data
   * @param source - Data source
   */
  private processIncomingData(devices: DeviceGpsInfo[], source: 'websocket' | 'api' | 'cache'): void {
    const now = Date.now();
    
    console.log(`🔄 [Provider] Processing incoming GPS data: ${devices.length} devices from ${source} at ${new Date().toISOString()}`);
    
    // Rate limiting check
    if (now - this.lastProcessTime < this.config.updateThreshold.interval) {
      if (this.config.enableBatching) {
        this.batchQueue.push(devices);
        console.log(`⏸️ [Provider] Rate limited (interval: ${this.config.updateThreshold.interval}ms), added to batch queue (${this.batchQueue.length} batches)`);
        return;
      } else {
        console.log(`⏸️ [Provider] Rate limited (interval: ${this.config.updateThreshold.interval}ms), skipping update`);
        return; // Skip update if too frequent
      }
    }
    
    // Prevent concurrent processing
    if (this.processingInProgress) {
      if (this.config.enableBatching) {
        this.batchQueue.push(devices);
        console.log(`⏸️ [Provider] Processing in progress, added to batch queue (${this.batchQueue.length} batches)`);
      }
      return;
    }
    
    this.processingInProgress = true;
    const startTime = Date.now();
    
    try {
      // Apply auto-filtering
      const filteredDevices = applyAutoFilter(devices, this.config.autoFilter);
      console.log(`🔍 [Provider] Auto-filtering: ${devices.length} → ${filteredDevices.length} devices`);
      
      // For real-time websocket data, we want to be more permissive about updates
      const isRealTimeWebsocket = source === 'websocket';
      
      // Process data updates and optimization
      const result = processGpsDataUpdates(
        this.previousDevices,
        filteredDevices,
        this.config.updateThreshold
      );
      
      console.log(`📈 [Provider] Processing result: hasSignificantChanges=${result.hasSignificantChanges}, added=${result.added.length}, updated=${result.updated.length}, removed=${result.removed.length}`);
      
      // Determine if we should update:
      // 1. Always update if not from websocket (cache/api)
      // 2. For websocket: update if significant changes OR if it's been a while since last update
      const timeSinceLastUpdate = this.lastUpdateTime ? now - this.lastUpdateTime.getTime() : Infinity;
      const forceUpdateInterval = 10000; // Force update every 10 seconds for real-time data
      const shouldForceUpdate = isRealTimeWebsocket && timeSinceLastUpdate > forceUpdateInterval;
      
      if (result.hasSignificantChanges || source !== 'websocket' || shouldForceUpdate) {
        const updateReason = result.hasSignificantChanges ? 'significant changes' : 
                            shouldForceUpdate ? 'forced update (10s interval)' : 
                            'non-websocket source';
        console.log(`✅ [Provider] Updating data and notifying callbacks (reason: ${updateReason})`);
        
        this.updateCurrentData(result.processed, source);
        
        // Cache the data
        if (this.config.enableCaching && source === 'websocket') {
          const cacheKey = generateCacheKey('user', undefined, 'realtime');
          this.cache.set(cacheKey, result.processed, source);
        }
        
        // Notify direct callbacks
        this.notifyUpdateCallbacks(result.processed);
      } else {
        this.stats.skippedUpdates++;
        console.log(`⏭️ [Provider] No significant changes, skipped update (total skipped: ${this.stats.skippedUpdates}, time since last: ${(timeSinceLastUpdate/1000).toFixed(1)}s)`);
      }
      
      // Update performance metrics
      const metrics = calculateProcessingMetrics(startTime, result);
      this.stats.optimizationRatio = metrics.optimizationRatio;
      this.stats.processedUpdates++;
      
      this.lastProcessTime = now;
      
    } catch (error) {
      console.error('❌ [Provider] Data processing error:', error);
      this.handleError(error instanceof Error ? error : new Error('Data processing failed'));
    } finally {
      this.processingInProgress = false;
    }
  }

  /**
   * Update current device data and statistics
   * @param devices - New device data
   * @param source - Data source
   */
  private updateCurrentData(devices: DeviceGpsInfo[], source: 'websocket' | 'api' | 'cache'): void {
    // Store previous data
    this.previousDevices = [...this.currentDevices];
    
    // Update current data
    this.currentDevices = devices;
    
    // Add to history
    if (this.config.enableCaching) {
      this.deviceHistory.push([...devices]);
      
      // Limit history size
      if (this.deviceHistory.length > this.config.cacheSize) {
        this.deviceHistory.shift();
      }
    }
    
    // Update statistics
    this.updateStatistics();
    
    // Track update rate
    this.trackUpdateRate();
    
    console.log(`📊 GPS data updated: ${devices.length} devices (source: ${source})`);
  }

  /**
   * Update provider statistics
   */
  private updateStatistics(): void {
    this.stats.lastUpdateTime = new Date();
    this.stats.updateCount++;
    this.stats.totalDevices = this.currentDevices.length;
    this.stats.activeDevices = this.currentDevices.filter(d => d.active === 'on').length;
    this.stats.inactiveDevices = this.stats.totalDevices - this.stats.activeDevices;
    this.stats.videoSharingDevices = this.currentDevices.filter(d => d.share_video === 'on').length;
    
    // Notify stats callbacks
    this.notifyStatsCallbacks();
    
    // Emit stats update event
    this.emitEvent('stats-update', this.stats);
  }

  /**
   * Track update rate for performance monitoring
   */
  private trackUpdateRate(): void {
    const now = Date.now();
    this.updateTimestamps.push(now);
    
    // Keep only last 60 timestamps (for 1-minute rate calculation)
    if (this.updateTimestamps.length > 60) {
      this.updateTimestamps.shift();
    }
    
    // Calculate updates per minute
    const oneMinuteAgo = now - 60000;
    const recentUpdates = this.updateTimestamps.filter(ts => ts > oneMinuteAgo);
    this.stats.updatesPerMinute = recentUpdates.length;
    
    // Calculate average interval
    if (this.updateTimestamps.length > 1) {
      const intervals: number[] = [];
      for (let i = 1; i < this.updateTimestamps.length; i++) {
        intervals.push(this.updateTimestamps[i] - this.updateTimestamps[i - 1]);
      }
      this.stats.averageUpdateInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    }
  }

  /**
   * Notify subscribers of connection changes
   * @param connected - Connection status
   */
  private notifyConnectionChange(connected: boolean): void {
    // Notify direct callbacks
    this.notifyConnectionCallbacks(connected);
    
    // Emit connection change event
    this.emitEvent('connection-change', connected);
  }

  /**
   * Handle errors and notify subscribers
   * @param error - Error to handle
   */
  private handleError(error: Error): void {
    this.stats.errorCount++;
    this.stats.lastError = error.message;
    
    console.error('❌ GPS Data Provider error:', error);
    
    // Notify direct callbacks
    this.notifyErrorCallbacks(error);
    
    // Emit error event
    this.emitEvent('error', error);
  }

  /**
   * Emit provider event
   * @param event - Event type
   * @param data - Event data
   */
  private emitEvent<T>(event: string, data: T): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Start batch processing
   */
  private startBatchProcessing(): void {
    if (this.batchTimer) return;
    
    this.batchTimer = setInterval(() => {
      if (this.batchQueue.length > 0) {
        const batches = [...this.batchQueue];
        this.batchQueue = [];
        
        // Process batched updates
        const result = batchProcessUpdates(batches, this.config.updateThreshold);
        
        if (result.hasSignificantChanges) {
          this.updateCurrentData(result.processed, 'websocket');
          this.notifyUpdateCallbacks(result.processed);
        }
      }
    }, this.config.batchInterval);
  }

  /**
   * Stop batch processing
   */
  private stopBatchProcessing(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  // === DIRECT CALLBACK NOTIFICATION METHODS ===

  /**
   * Notify all update callbacks with new device data
   * @param devices - Updated device data
   */
  private notifyUpdateCallbacks(devices: DeviceGpsInfo[]): void {
    if (this.updateCallbacks.size === 0) return;
    
    console.log(`📢 [Provider] Notifying ${this.updateCallbacks.size} update callbacks with ${devices.length} devices at ${new Date().toISOString()}`);
    
    // Apply optimization for callback data
    const optimizedDevices = optimizeForMapRendering(devices, {
      maxDevices: this.config.maxDevicesPerUpdate,
      prioritizeActive: true,
      prioritizeVideo: true
    });
    
    console.log(`📦 [Provider] Optimized to ${optimizedDevices.length} devices for callbacks`);
    
    this.updateCallbacks.forEach(callback => {
      try {
        callback(optimizedDevices);
        console.log('✅ [Provider] Update callback executed successfully');
      } catch (error) {
        console.error('❌ [Provider] Error in GPS data update callback:', error);
      }
    });
  }

  /**
   * Notify all connection callbacks with connection status
   * @param connected - Connection status
   */
  private notifyConnectionCallbacks(connected: boolean): void {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in GPS connection callback:', error);
      }
    });
  }

  /**
   * Notify all error callbacks with error information
   * @param error - Error to notify
   */
  private notifyErrorCallbacks(error: Error): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (error) {
        console.error('Error in GPS error callback:', error);
      }
    });
  }

  /**
   * Notify all stats callbacks with updated statistics
   */
  private notifyStatsCallbacks(): void {
    this.statsCallbacks.forEach(callback => {
      try {
        callback(this.stats);
      } catch (error) {
        console.error('Error in GPS stats callback:', error);
      }
    });
  }
}